import React, { useMemo } from 'react';
import { Notification } from '../../../models/Notification';
import { makePrioStyles } from '../../../theme/utils';
import { AbsenceProposal } from '../../../models/AbsenceProposal';
import Flex from '../../../components/Flex';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Input,
  Modal,
  Typography,
  notification as antNotification,
  Divider,
} from 'antd';
import { ContactId } from '../../../models/Types';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import {
  compactDateWithWeekDayFormatString,
  fullDateFormatFormatString,
} from '../../../util';
import { Link } from 'react-router-dom';
import { User } from '../../../models/User';
import { useDispatch } from 'react-redux';
import { Contact } from '../../../models/Contact';
import { markReadNotifications, markReadNotificationsLocal } from '../actions';
import {
  apiAcceptAbsenceProposal,
  apiAcceptOfficeAbsenceProposal,
  apiDeclineAbsenceProposal,
  apiDeclineOfficeAbsenceProposal,
} from '../../absences/api';
import useAccessRights from '../../users/hooks/useAccessRights';
import { useTheme } from 'react-jss';
import { PrioTheme } from '../../../theme/types';
import { getIconForAbsenceType } from '../../../util/icon';
import useContactsContext from '../../contacts/hooks/useContactsProvider';

const useStyles = makePrioStyles((theme) => ({
  root: {
    backgroundColor: theme.old.palette.backgroundPalette.sub,
    padding: theme.old.spacing.unit(2),
  },
  secondaryText: {
    color: theme.old.typography.colors.muted,
    fontSize: theme.old.typography.fontSize.label,
  },
  decline: {
    '&.ant-btn:not(.ant-btn-primary) > .svg-inline--fa': {
      color: theme.old.palette.chromaticPalette.red,
    },
  },
  accept: {
    '& > .prio-button-icon': {
      color: theme.old.palette.chromaticPalette.green,
    },
    '&:hover': {
      backgroundColor: 'transparent',
      '& > .prio-button-icon': {
        color: theme.old.palette.chromaticPalette.green,
      },
    },
  },
  textAfterIcon: {
    // marginLeft: theme.old.spacing.defaultPadding,
  },
  iconBeforeText: {
    margin: 'auto 0',
    color: theme.old.palette.chromaticPalette.grey,
  },
}));

interface AbsenceNotificationItemProps {
  notification: Notification;
  onClose?: VoidFunction;
  userMe?: User;
}

export const AbsenceNotificationItem: React.FC<AbsenceNotificationItemProps> = (
  props
) => {
  const classes = useStyles();
  const theme = useTheme<PrioTheme>();
  const { t } = useTranslation();

  const { notification, onClose, userMe } = props;

  const dispatch = useDispatch();

  const proposal = notification.data as AbsenceProposal;

  const { getContactById } = useContactsContext();

  const contact = useMemo(() => {
    const contactId = getContactId(proposal, userMe);
    return getContactById(contactId);
  }, [proposal, userMe, getContactById]);

  const contactSubstitude = useMemo(() => {
    return getContactById(proposal.substituteId);
  }, [proposal.substituteId, getContactById]);

  const { canManageAbsenceProposalOffice, canManageAbsenceProposalGlobal } =
    useAccessRights(
      ['canManageAbsenceProposalOffice', 'canManageAbsenceProposalGlobal'],
      proposal?.officeId ? { officeId: proposal?.officeId } : undefined
    );

  const parsedProposal = getParsedProposal(
    proposal,
    contact,
    userMe,
    t,
    canManageAbsenceProposalGlobal,
    canManageAbsenceProposalOffice
  );

  const showModalDecline = () => {
    let isApiCallInProgress = false;
    Modal.confirm({
      maskClosable: true,
      icon: null,
      title: (
        <>
          <Flex.Row childrenGap={theme.old.spacing.unit(2)}>
            <FontAwesomeIcon
              icon={['fal', getIconForAbsenceType(proposal.absenceType)]}
              color={theme.old.palette.primaryColor}
            />
            <span className={classes.textAfterIcon}>
              {t(
                `absences:notification.modal.decline.${proposal.absenceState}.${proposal.absenceType}`
              )}
            </span>
          </Flex.Row>
        </>
      ),
      content: (
        <>
          <Flex.Row childrenGap={theme.old.spacing.unit(2)}>
            <Divider></Divider>
          </Flex.Row>
          <Flex.Row childrenGap={theme.old.spacing.unit(2)}>
            <FontAwesomeIcon
              icon={['fal', 'user']}
              className={classes.iconBeforeText}
            />
            <span className={classes.textAfterIcon}>
              {contact.firstName} {contact.lastName}
            </span>
          </Flex.Row>
          <Flex.Row childrenGap={theme.old.spacing.unit(2)}>
            <FontAwesomeIcon
              icon={['fal', 'arrow-from-left']}
              className={classes.iconBeforeText}
            />
            <span className={classes.textAfterIcon}>
              {compactDateWithWeekDayFormatString(proposal.from)}
            </span>
            <span className={classes.textAfterIcon}> - </span>
            <span className={classes.textAfterIcon}>
              {compactDateWithWeekDayFormatString(proposal.to)}
            </span>
          </Flex.Row>
          {proposal.absenceType === 'annualLeave' && (
            <Flex.Row childrenGap={theme.old.spacing.unit(2)}>
              <FontAwesomeIcon
                icon={['fal', 'calendar-day']}
                className={classes.iconBeforeText}
              />
              <span className={classes.textAfterIcon}>
                {proposal.absentWorkdays}{' '}
                {t(`absences:notification.modal.accept.days`)}
              </span>
            </Flex.Row>
          )}
          <Flex.Row childrenGap={theme.old.spacing.unit(2)}>
            <FontAwesomeIcon
              icon={['fal', 'people-arrows']}
              className={classes.iconBeforeText}
            />
            <span className={classes.textAfterIcon}>
              {contactSubstitude.firstName} {contactSubstitude.lastName}
            </span>
          </Flex.Row>
          <Flex.Row childrenGap={theme.old.spacing.unit(2)}>
            <Divider></Divider>
          </Flex.Row>
          <Flex.Row childrenGap={theme.old.spacing.unit(2)}>
            <FontAwesomeIcon
              icon={['fal', 'info-circle']}
              className={classes.iconBeforeText}
            />
            <span className={classes.textAfterIcon}>
              {t(
                `absences:notification.modal.declineContent.${proposal.absenceState}.${proposal.absenceType}`,
                {
                  name: contact.firstName + ' ' + contact.lastName,
                }
              )}
            </span>
          </Flex.Row>
          <Flex.Row childrenGap={theme.old.spacing.unit(2)}>
            <Divider></Divider>
          </Flex.Row>
          <Flex.Row>
            <Input.TextArea
              placeholder={t('absences:notification.modal.content')}
            />
          </Flex.Row>
        </>
      ),
      okText: t('absences:notification.modal.okAction'),
      cancelText: t('common:actions.cancel'),
      async onOk() {
        if (isApiCallInProgress) return;
        isApiCallInProgress = true;
        const promise = new Promise<void>(async (resolve) => {
          if (
            !canManageAbsenceProposalGlobal &&
            !canManageAbsenceProposalOffice
          ) {
            resolve();
            return;
          }
          const { result } = canManageAbsenceProposalGlobal
            ? await apiDeclineAbsenceProposal(proposal.absenceProposalId)
            : await apiDeclineOfficeAbsenceProposal(
                proposal.absenceProposalId,
                proposal.officeId
              );
          if (result.status >= 200 && result.status < 300) {
            dispatch(
              markReadNotificationsLocal(
                notification.notificationId,
                notification
              )
            );
          } else {
            antNotification.open({
              message: t('common:error'),
              description: t(
                'absences:errorMessages.declineAbsenceProposalError'
              ),
            });
          }
          resolve();
        });
        return promise;
      },
      onCancel() {},
    });
  };

  const showModalAccept = () => {
    let isApiCallInProgress = false;
    Modal.confirm({
      maskClosable: true,
      icon: null,
      title: (
        <>
          <Flex.Row childrenGap={theme.old.spacing.unit(2)}>
            <FontAwesomeIcon
              icon={['fal', getIconForAbsenceType(proposal.absenceType)]}
              color={theme.old.palette.primaryColor}
            />
            <span className={classes.textAfterIcon}>
              {t(
                `absences:notification.modal.accept.${proposal.absenceState}.${proposal.absenceType}`
              )}
            </span>
          </Flex.Row>
        </>
      ),
      content: (
        <>
          <Flex.Row childrenGap={theme.old.spacing.unit(2)}>
            <Divider></Divider>
          </Flex.Row>
          <Flex.Row childrenGap={theme.old.spacing.unit(2)}>
            <FontAwesomeIcon
              icon={['fal', 'user']}
              className={classes.iconBeforeText}
            />
            <span className={classes.textAfterIcon}>
              {contact.firstName} {contact.lastName}
            </span>
          </Flex.Row>
          <Flex.Row childrenGap={theme.old.spacing.unit(2)}>
            <FontAwesomeIcon
              icon={['fal', 'arrow-from-left']}
              className={classes.iconBeforeText}
            />
            <span className={classes.textAfterIcon}>
              {compactDateWithWeekDayFormatString(proposal.from)}
            </span>
            <span className={classes.textAfterIcon}> - </span>
            <span className={classes.textAfterIcon}>
              {compactDateWithWeekDayFormatString(proposal.to)}
            </span>
          </Flex.Row>
          {proposal.absenceType === 'annualLeave' && (
            <Flex.Row childrenGap={theme.old.spacing.unit(2)}>
              <FontAwesomeIcon
                icon={['fal', 'calendar-day']}
                className={classes.iconBeforeText}
              />
              <span className={classes.textAfterIcon}>
                {proposal.absentWorkdays}{' '}
                {t(`absences:notification.modal.accept.days`)}
              </span>
            </Flex.Row>
          )}
          <Flex.Row childrenGap={theme.old.spacing.unit(2)}>
            <FontAwesomeIcon
              icon={['fal', 'people-arrows']}
              className={classes.iconBeforeText}
            />
            <span className={classes.textAfterIcon}>
              {contactSubstitude.firstName} {contactSubstitude.lastName}
            </span>
          </Flex.Row>
          <Flex.Row childrenGap={theme.old.spacing.unit(2)}>
            <Divider></Divider>
          </Flex.Row>
          <Flex.Row childrenGap={theme.old.spacing.unit(2)}>
            <FontAwesomeIcon
              icon={['fal', 'info-circle']}
              className={classes.iconBeforeText}
            />
            <span className={classes.textAfterIcon}>
              {t(
                `absences:notification.modal.acceptContent.${proposal.absenceState}.${proposal.absenceType}`,
                {
                  name: contact.firstName + ' ' + contact.lastName,
                }
              )}
            </span>
          </Flex.Row>
        </>
      ),
      okText: t('absences:notification.modal.okText'),
      cancelText: t('absences:notification.modal.cancelText'),
      async onOk() {
        if (isApiCallInProgress) return;
        isApiCallInProgress = true;
        const promise = new Promise<void>(async (resolve) => {
          if (
            !canManageAbsenceProposalGlobal &&
            !canManageAbsenceProposalOffice
          ) {
            resolve();
            return;
          }
          const { result } = canManageAbsenceProposalGlobal
            ? await apiAcceptAbsenceProposal(proposal.absenceProposalId)
            : await apiAcceptOfficeAbsenceProposal(
                proposal.absenceProposalId,
                proposal.officeId
              );
          if (result.status >= 200 && result.status < 300) {
            dispatch(
              markReadNotificationsLocal(
                notification.notificationId,
                notification
              )
            );
          } else {
            antNotification.open({
              message: t('common:error'),
              description: t(
                'absences:errorMessages.acceptAbsenceProposalError'
              ),
            });
          }
          resolve();
        });
        return promise;
      },
      onCancel() {},
    });
  };

  const render = () => (
    <Flex.Row
      className={classes.root}
      alignItems="center"
      childrenGap={theme.old.spacing.unit(2)}
    >
      <Flex.Item>
        {proposal.applicantId === userMe.id ? (
          <FontAwesomeIcon
            icon={['fal', getIconForAbsenceType(proposal.absenceType)]}
            color={theme.old.palette.primaryColor}
          />
        ) : proposal.principalId === userMe.id ? (
          <FontAwesomeIcon
            icon={['fal', getIconForAbsenceType(proposal.absenceType)]}
            color={theme.old.palette.primaryColor}
          />
        ) : (
          <FontAwesomeIcon
            icon={['fal', 'sign-out']}
            color={theme.old.palette.primaryColor}
          />
        )}
      </Flex.Item>
      <Flex.Item flex={1}>
        <Flex.Column>
          <Typography.Text ellipsis>
            {parsedProposal?.name ?? ''}
          </Typography.Text>
          {parsedProposal?.description && (
            <Typography.Text className={classes.secondaryText}>
              {parsedProposal.description}
            </Typography.Text>
          )}
        </Flex.Column>
      </Flex.Item>
      <Flex.Item flex={1}>
        <Flex.Column>{getDetails(proposal, t)}</Flex.Column>
      </Flex.Item>
      <Flex.Item flex={0.1}>
        <Flex.Row>
          {parsedProposal?.actions.includes('decline') && (
            <Button
              type="link"
              className={classes.decline}
              icon={<FontAwesomeIcon icon={['fal', 'times']} />}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                showModalDecline();
              }}
            />
          )}
          {parsedProposal?.actions.includes('accept') && (
            <Button
              type="link"
              className={classes.accept}
              icon={<FontAwesomeIcon icon={['fal', 'check']} />}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                showModalAccept();
              }}
            />
          )}
          {parsedProposal?.actions.includes('read') && (
            <Button
              type="link"
              className={classes.accept}
              icon={<FontAwesomeIcon icon={['fal', 'check']} />}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                dispatch(
                  markReadNotifications(notification.notificationId, {
                    ...notification,
                    isRead: true,
                  })
                );
              }}
            />
          )}
        </Flex.Row>
      </Flex.Item>
    </Flex.Row>
  );

  if (parsedProposal?.link) {
    return (
      <Link to={parsedProposal.link} onClick={onClose}>
        {render()}
      </Link>
    );
  }
  return render();
};

const getContactId: (proposal: AbsenceProposal, userMe: User) => ContactId = (
  proposal,
  userMe
) => {
  switch (proposal.absenceState) {
    case 'revokeRequested':
    case 'requested': {
      return proposal.applicantId;
    }
    case 'declined': {
      return proposal.principalId;
    }
    case 'revokeAccepted':
    case 'accepted': {
      if (userMe.id === proposal.applicantId) {
        return proposal.approvedBy ?? proposal.principalId;
      } else {
        return proposal.applicantId;
      }
    }
    default: {
      return proposal.approvedBy ?? proposal.principalId;
    }
  }
};
interface AbsenceNotification {
  name: string;
  description?: string;
  link?: string;
  actions: Array<'accept' | 'decline' | 'read'>;
}
const getParsedProposal: (
  proposal: AbsenceProposal,
  contact: Contact,
  userMe: User,
  t: TFunction,
  globalAccess: boolean,
  officeAccess: boolean
) => AbsenceNotification = (proposal, contact, userMe, t, globalAccess, officeAccess) => {
  const type: string = t(`absences:types.${proposal.absenceType}`);
  const name: string = contact
    ? `${contact.firstName} ${contact.lastName}`
    : t('common:unknownContact');
  const isPendingApproval =
    proposal.absenceState === 'requested' ||
    proposal.absenceState === 'revokeRequested';
  const canApprove = globalAccess || officeAccess;
  const approvalActions: Array<'accept' | 'decline' | 'read'> =
    isPendingApproval && canApprove ? ['accept', 'decline'] : ['read'];

  switch (proposal.absenceState) {
    case 'revokeRequested':
    case 'requested':
    case 'revokeAccepted':
    case 'declined':
    case 'accepted': {
      if (
        userMe.id === proposal.applicantId &&
        userMe.id !== proposal.principalId
      ) {
        return {
          name: t(
            `absences:notification.principalOrMe.${proposal.absenceType}.${proposal.absenceState}`,
            {
              type: type,
            }
          ),
          description:
            proposal.absenceState === 'accepted'
              ? t('absences:notification.typesActions.acceptedBy', {
                  name: name,
                })
              : proposal.absenceState === 'declined'
                ? t('absences:notification.declined.declinedBy', {
                    name: name,
                  })
                : undefined,
          actions: ['read'],
        };
      } else if (
        userMe.id === proposal.applicantId &&
        userMe.id === proposal.principalId
      ) {
        return {
          name: t(
            `absences:notification.principalOrMe.${proposal.absenceType}.${proposal.absenceState}`
          ),
          description: name,
          actions: approvalActions,
        };
      } else if (
        userMe.id === proposal.substituteId &&
        userMe.id !== proposal.principalId
      ) {
        return {
          name: t(
            `absences:notification.substitude.${proposal.absenceType}.${proposal.absenceState}`
          ),
          description: name,
          actions: ['read'],
        };
      } else if (
        userMe.id === proposal.substituteId &&
        userMe.id === proposal.principalId
      ) {
        return {
          name: isPendingApproval
            ? t(
                `absences:notification.principalOrMe.${proposal.absenceType}.${proposal.absenceState}`
              )
            : t(
                `absences:notification.substitude.${proposal.absenceType}.${proposal.absenceState}`
              ),
          description: name,
          actions: approvalActions,
        };
      } else if (userMe.id === proposal.principalId) {
        return {
          name: t(
            `absences:notification.principalOrMe.${proposal.absenceType}.${proposal.absenceState}`
          ),
          description: name,
          actions: approvalActions,
        };
      } else {
        return {
          name: t(
            `absences:notification.other.${proposal.absenceType}.${proposal.absenceState}`
          ),
          description: name,
          actions: ['read'],
        };
      }
    }
    default:
      return null;
  }
};

const getDetails: (
  proposal: AbsenceProposal,
  t: TFunction
) => React.ReactNode = (proposal, t) => {
  return (
    <>
      <Flex.Item flex={1}></Flex.Item>
      <Flex.Item flex={3}>
        {proposal.from === proposal.to ? (
          <>
            {fullDateFormatFormatString(proposal.from)}
            {(proposal.firstIsHalfDay || proposal.lastIsHalfDay) && (
              <span> ({t('absences:notification.halfDayText')})</span>
            )}
          </>
        ) : (
          <>
            {fullDateFormatFormatString(proposal.from)}
            {proposal.firstIsHalfDay && (
              <span> ({t('absences:notification.halfDayText')})</span>
            )}{' '}
            {t('common:to')} {fullDateFormatFormatString(proposal.to)}
            {proposal.lastIsHalfDay && (
              <span> ({t('absences:notification.halfDayText')})</span>
            )}
          </>
        )}
      </Flex.Item>
    </>
  );
};

export default AbsenceNotificationItem;
